import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import { ArrowUpRight, FileText, LoaderCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    VOIP_PHONE_PROVIDER_LABELS,
    VOIP_PHONE_PROVIDER_OPTIONS,
} from '@apps/web/components/admin/voip/voip_utils';
import { AdminBadge } from '@apps/web/features/admin_design/admin_badge';
import {
    AdminInput,
    AdminSearchInput,
    AdminSelect,
} from '@apps/web/features/admin_design/admin_controls';
import {
    AdminPage,
    AdminPageBody,
    AdminPageHeader,
} from '@apps/web/features/admin_design/admin_shell';
import { AdminTableSkeleton } from '@apps/web/features/admin_design/admin_skeleton';
import {
    AdminEmptyState,
    AdminErrorState,
} from '@apps/web/features/admin_design/admin_states';
import {
    AdminTable,
    AdminTableBody,
    AdminTableCell,
    AdminTableHeadCell,
    AdminTableHeader,
    AdminTableRow,
    AdminTableSortableHeadCell,
    type AdminTableSortDirection,
} from '@apps/web/features/admin_design/admin_table';
import { AdminTabs } from '@apps/web/features/admin_design/admin_tabs';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useAdminVoipAccounts } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type VoipAccountRead,
    type VoipPhoneProvider,
} from '@libraries/typescript/api_client';

type MetricWindowDays = 7 | 30 | 90;
type ActivationView = 'all' | 'setup' | 'no-calls' | 'transfers';

export const Route = createFileRoute('/_app/admin/voip/')({
    head: () => ({ meta: [{ title: 'Voice activation | Phoebe Admin' }] }),
    component: AdminVoipDashboardPage,
});

const VOICE_ACTIVATION_COLUMNS = [
    'Organization',
    'Notes',
    'Provider',
    'Setup',
    'Inbound calls',
    'Phoebe outbound',
    'Inbound transfer',
    'Last inbound',
] as const;

type EditableActivationUpdate = {
    readonly phone_provider?: VoipPhoneProvider | null;
    readonly voip_setup_complete?: boolean | null;
    readonly status_notes?: string;
};

type EditableActivationField = keyof EditableActivationUpdate;

export type VoiceActivationMetricAccount = Pick<
    VoipAccountRead,
    | 'inbound_call_count'
    | 'phoebe_outbound_call_count'
    | 'inbound_transferred_call_count'
    | 'inbound_not_transferred_call_count'
    | 'inbound_transfer_unknown_call_count'
    | 'voip_setup_complete'
>;

export function formatDailyAverage(total: number, days: number): string {
    const average = total / days;
    if (average === 0) {
        return '0';
    }
    if (average < 10) {
        return average.toFixed(1);
    }
    return average.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export function matchesActivationView(
    account: VoiceActivationMetricAccount,
    view: ActivationView,
): boolean {
    if (view === 'setup') {
        return account.voip_setup_complete !== true;
    }
    if (view === 'no-calls') {
        return (
            account.inbound_call_count + account.phoebe_outbound_call_count ===
            0
        );
    }
    if (view === 'transfers') {
        return account.inbound_transferred_call_count > 0;
    }
    return true;
}

export function classifiedTransferPercent(
    transferred: number,
    notTransferred: number,
): number | null {
    const classified = transferred + notTransferred;
    return classified === 0 ? null : (transferred / classified) * 100;
}

/**
 * Project the selected metric window's counts onto the flat fields the
 * table renders, so switching windows never refetches. Falls back to the
 * flat fields (the server's default 30-day window) when a response from an
 * older API task carries no per-window metrics.
 */
export function applyMetricWindow(
    account: VoipAccountRead,
    days: MetricWindowDays,
): VoipAccountRead {
    const windowMetrics = (account.call_metrics_windows ?? []).find(
        (candidate) => candidate.window_days === days,
    );
    if (!windowMetrics) {
        return account;
    }
    return {
        ...account,
        metric_window_days: days,
        inbound_call_count: windowMetrics.inbound_call_count,
        inbound_forwarded_call_count:
            windowMetrics.inbound_forwarded_call_count,
        phoebe_outbound_call_count: windowMetrics.phoebe_outbound_call_count,
        inbound_transferred_call_count:
            windowMetrics.inbound_transferred_call_count,
        inbound_not_transferred_call_count:
            windowMetrics.inbound_not_transferred_call_count,
        inbound_transfer_unknown_call_count:
            windowMetrics.inbound_transfer_unknown_call_count,
        last_inbound_call_at: windowMetrics.last_inbound_call_at,
    };
}

export type ActivationSortColumn =
    | 'organization'
    | 'provider'
    | 'setup'
    | 'inbound-calls'
    | 'phoebe-outbound'
    | 'inbound-transfer'
    | 'last-inbound';

export type ActivationSort = {
    readonly column: ActivationSortColumn;
    readonly direction: AdminTableSortDirection;
};

/** Text-like columns read naturally ascending; metrics start with the
 * biggest or most recent values first. */
export function defaultActivationSortDirection(
    column: ActivationSortColumn,
): AdminTableSortDirection {
    return column === 'organization' ||
        column === 'provider' ||
        column === 'setup'
        ? 'asc'
        : 'desc';
}

function activationSortValue(
    account: VoipAccountRead,
    column: ActivationSortColumn,
): string | number | null {
    switch (column) {
        case 'organization':
            return (
                account.organization_name ?? account.account_name
            ).toLocaleLowerCase();
        case 'provider':
            return account.phone_provider
                ? VOIP_PHONE_PROVIDER_LABELS[
                      account.phone_provider
                  ].toLocaleLowerCase()
                : null;
        case 'setup':
            return account.voip_setup_complete === true
                ? 0
                : account.voip_setup_complete === false
                  ? 1
                  : 2;
        case 'inbound-calls':
            return account.inbound_call_count;
        case 'phoebe-outbound':
            return account.phoebe_outbound_call_count;
        case 'inbound-transfer':
            return account.inbound_call_count === 0
                ? null
                : classifiedTransferPercent(
                      account.inbound_transferred_call_count,
                      account.inbound_not_transferred_call_count,
                  );
        case 'last-inbound':
            return account.last_inbound_call_at
                ? Date.parse(account.last_inbound_call_at)
                : null;
        default: {
            const exhaustive: never = column;
            return exhaustive;
        }
    }
}

export function compareActivationAccounts(
    a: VoipAccountRead,
    b: VoipAccountRead,
    sort: ActivationSort,
): number {
    const aValue = activationSortValue(a, sort.column);
    const bValue = activationSortValue(b, sort.column);
    // Rows without a comparable value (no provider, no classified
    // transfers, no inbound calls) sink to the bottom in either direction.
    if (aValue === null || bValue === null) {
        return (aValue === null ? 1 : 0) - (bValue === null ? 1 : 0);
    }
    const comparison =
        typeof aValue === 'string' || typeof bValue === 'string'
            ? String(aValue).localeCompare(String(bValue))
            : aValue - bValue;
    return sort.direction === 'asc' ? comparison : -comparison;
}

function editableFieldKey(
    account: VoipAccountRead,
    field: EditableActivationField,
) {
    return `${account.organization_id ?? account.id ?? account.account_name}:${field}`;
}

function voipAccountUpdateBody(
    account: VoipAccountRead,
    update: EditableActivationUpdate,
) {
    const updated = { ...account, ...update };
    return {
        account_name: updated.account_name,
        organization_id: updated.organization_id ?? null,
        contacts: updated.contacts,
        cx_owner: updated.cx_owner ?? null,
        eng_owner: updated.eng_owner ?? null,
        goal: updated.goal,
        phone_provider: updated.phone_provider ?? null,
        integration_type: updated.integration_type ?? null,
        status_notes: updated.status_notes,
    };
}

export function AdminVoipDashboardPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [metricWindowDays, setMetricWindowDays] =
        useState<MetricWindowDays>(30);
    const [providerFilter, setProviderFilter] = useState<
        VoipPhoneProvider | 'all'
    >('all');
    const [activationView, setActivationView] = useState<ActivationView>('all');
    const [sort, setSort] = useState<ActivationSort | null>(null);
    const [savingFields, setSavingFields] = useState<ReadonlySet<string>>(
        () => new Set(),
    );

    const {
        data: accountsResponse,
        isLoading,
        error,
        mutate,
    } = useAdminVoipAccounts();
    const accounts = useMemo(
        () =>
            (accountsResponse?.accounts ?? []).map((account) =>
                applyMetricWindow(account, metricWindowDays),
            ),
        [accountsResponse?.accounts, metricWindowDays],
    );

    const baseFilteredAccounts = useMemo(() => {
        const needle = searchQuery.trim().toLocaleLowerCase();
        return accounts.filter((account) => {
            if (
                providerFilter !== 'all' &&
                account.phone_provider !== providerFilter
            ) {
                return false;
            }
            if (!needle) {
                return true;
            }
            return [
                account.account_name,
                account.organization_name,
                account.organization_internal_name,
                account.status_notes,
                account.phone_provider
                    ? VOIP_PHONE_PROVIDER_LABELS[account.phone_provider]
                    : null,
            ]
                .filter((value): value is string => Boolean(value))
                .some((value) => value.toLocaleLowerCase().includes(needle));
        });
    }, [accounts, providerFilter, searchQuery]);

    const visibleAccounts = useMemo(() => {
        const matching = baseFilteredAccounts.filter((account) =>
            matchesActivationView(account, activationView),
        );
        return sort
            ? matching.sort((a, b) => compareActivationAccounts(a, b, sort))
            : matching;
    }, [activationView, baseFilteredAccounts, sort]);

    const handleSort = useCallback((column: ActivationSortColumn) => {
        setSort((current) =>
            current?.column === column
                ? {
                      column,
                      direction: current.direction === 'asc' ? 'desc' : 'asc',
                  }
                : { column, direction: defaultActivationSortDirection(column) },
        );
    }, []);

    const tabItems = useMemo(
        () =>
            [
                {
                    value: 'all',
                    label: tabLabel('All', baseFilteredAccounts.length),
                },
                {
                    value: 'setup',
                    label: tabLabel(
                        'Setup incomplete',
                        baseFilteredAccounts.filter((account) =>
                            matchesActivationView(account, 'setup'),
                        ).length,
                    ),
                },
                {
                    value: 'no-calls',
                    label: tabLabel(
                        'No recent calls',
                        baseFilteredAccounts.filter((account) =>
                            matchesActivationView(account, 'no-calls'),
                        ).length,
                    ),
                },
                {
                    value: 'transfers',
                    label: tabLabel(
                        'Has transfers',
                        baseFilteredAccounts.filter((account) =>
                            matchesActivationView(account, 'transfers'),
                        ).length,
                    ),
                },
            ] as const,
        [baseFilteredAccounts],
    );

    const saveActivationUpdate = useCallback(
        async (
            account: VoipAccountRead,
            field: EditableActivationField,
            update: EditableActivationUpdate,
        ) => {
            const key = editableFieldKey(account, field);
            setSavingFields((current) => new Set(current).add(key));
            const applyToRow = (
                apply: (candidate: VoipAccountRead) => VoipAccountRead,
            ) =>
                mutate(
                    (current) =>
                        current
                            ? {
                                  ...current,
                                  accounts: current.accounts.map((candidate) =>
                                      candidate.organization_id ===
                                          account.organization_id &&
                                      candidate.id === account.id
                                          ? apply(candidate)
                                          : candidate,
                                  ),
                              }
                            : current,
                    false,
                );
            await applyToRow((candidate) => ({ ...candidate, ...update }));
            try {
                // Apply the authoritative response to the edited row in
                // place; refetching the whole list recomputes every
                // organization's call rollup server-side.
                if (account.organization_id) {
                    const saved = await getApiClient().patch({
                        url: '/admin/voip/organizations/{organization_id}/activation',
                        pathParams: {
                            organization_id: account.organization_id,
                        },
                        body: update,
                    });
                    await applyToRow((candidate) => ({
                        ...candidate,
                        id: saved.voip_account_id ?? candidate.id,
                        phone_provider: saved.phone_provider ?? null,
                        voip_setup_complete: saved.voip_setup_complete ?? null,
                        status_notes: saved.status_notes,
                        provider_conflict: saved.provider_conflict,
                    }));
                } else if (account.id && field !== 'voip_setup_complete') {
                    const saved = await getApiClient().put({
                        url: '/admin/voip/accounts/{voip_account_id}',
                        pathParams: { voip_account_id: account.id },
                        body: voipAccountUpdateBody(account, update),
                    });
                    await applyToRow(() => saved);
                }
            } catch {
                toast.error('Could not save the voice activation field', {
                    persistNotification: false,
                });
                // Roll the optimistic update back with server truth.
                await mutate();
            } finally {
                setSavingFields((current) => {
                    const next = new Set(current);
                    next.delete(key);
                    return next;
                });
            }
        },
        [mutate],
    );

    return (
        <AdminPage>
            <AdminPageHeader
                title="Voice activation"
                meta={`${visibleAccounts.length.toLocaleString()} of ${accounts.length.toLocaleString()} organizations`}
            >
                <div className="flex flex-wrap items-center gap-2">
                    <AdminSearchInput
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        onClear={() => setSearchQuery('')}
                        placeholder="Search organizations or notes…"
                        aria-label="Search voice activation organizations"
                        containerClassName="w-full sm:w-72"
                    />
                    <AdminSelect
                        aria-label="Voice metric period"
                        value={String(metricWindowDays)}
                        onValueChange={(value) =>
                            setMetricWindowDays(
                                Number(value) as MetricWindowDays,
                            )
                        }
                        options={[
                            { value: '7', label: 'Last 7 days' },
                            { value: '30', label: 'Last 30 days' },
                            { value: '90', label: 'Last 90 days' },
                        ]}
                        className="w-36"
                    />
                    <AdminSelect
                        aria-label="Filter by VoIP provider"
                        value={providerFilter}
                        onValueChange={(value) =>
                            setProviderFilter(
                                value as VoipPhoneProvider | 'all',
                            )
                        }
                        options={[
                            { value: 'all', label: 'All providers' },
                            ...VOIP_PHONE_PROVIDER_OPTIONS,
                        ]}
                        className="w-44"
                    />
                </div>
                <AdminTabs
                    items={tabItems}
                    value={activationView}
                    onValueChange={(value) =>
                        setActivationView(value as ActivationView)
                    }
                    aria-label="Voice activation views"
                />
            </AdminPageHeader>
            <AdminPageBody padded={false}>
                {isLoading ? (
                    <AdminTableSkeleton
                        columns={VOICE_ACTIVATION_COLUMNS}
                        rows={8}
                    />
                ) : error ? (
                    <AdminErrorState
                        title="Failed to load voice activation"
                        description="The organization metrics could not be loaded."
                        onRetry={() => void mutate()}
                    />
                ) : visibleAccounts.length === 0 ? (
                    <AdminEmptyState
                        icon={FileText}
                        title="No organizations match"
                        description="Try another activation view, provider, or search."
                    />
                ) : (
                    <VoiceActivationTable
                        accounts={visibleAccounts}
                        metricWindowDays={metricWindowDays}
                        savingFields={savingFields}
                        onSave={saveActivationUpdate}
                        sort={sort}
                        onSort={handleSort}
                    />
                )}
            </AdminPageBody>
        </AdminPage>
    );
}

function tabLabel(label: string, count: number) {
    return (
        <span className="inline-flex items-center gap-1.5">
            {label}
            <span className="text-admin-xs text-admin-ink-faint tabular-nums">
                {count.toLocaleString()}
            </span>
        </span>
    );
}

function ActivationSortHeadCell({
    column,
    sort,
    onSort,
    className,
    children,
}: {
    readonly column: ActivationSortColumn;
    readonly sort: ActivationSort | null;
    readonly onSort: (column: ActivationSortColumn) => void;
    readonly className?: string;
    readonly children: React.ReactNode;
}) {
    const active = sort !== null && sort.column === column;
    return (
        <AdminTableSortableHeadCell
            className={className}
            active={active}
            direction={
                active ? sort.direction : defaultActivationSortDirection(column)
            }
            onSort={() => onSort(column)}
        >
            {children}
        </AdminTableSortableHeadCell>
    );
}

function VoiceActivationTable({
    accounts,
    metricWindowDays,
    savingFields,
    onSave,
    sort,
    onSort,
}: {
    readonly accounts: ReadonlyArray<VoipAccountRead>;
    readonly metricWindowDays: MetricWindowDays;
    readonly savingFields: ReadonlySet<string>;
    readonly onSave: (
        account: VoipAccountRead,
        field: EditableActivationField,
        update: EditableActivationUpdate,
    ) => Promise<void>;
    readonly sort: ActivationSort | null;
    readonly onSort: (column: ActivationSortColumn) => void;
}) {
    return (
        <AdminTable className="min-w-[1360px] table-fixed">
            <AdminTableHeader>
                <tr>
                    <ActivationSortHeadCell
                        column="organization"
                        sort={sort}
                        onSort={onSort}
                        className="w-[230px]"
                    >
                        Organization
                    </ActivationSortHeadCell>
                    <AdminTableHeadCell className="w-[260px]">
                        Notes
                    </AdminTableHeadCell>
                    <ActivationSortHeadCell
                        column="provider"
                        sort={sort}
                        onSort={onSort}
                        className="w-[150px]"
                    >
                        Provider
                    </ActivationSortHeadCell>
                    <ActivationSortHeadCell
                        column="setup"
                        sort={sort}
                        onSort={onSort}
                        className="w-[130px]"
                    >
                        Setup
                    </ActivationSortHeadCell>
                    <ActivationSortHeadCell
                        column="inbound-calls"
                        sort={sort}
                        onSort={onSort}
                        className="w-[140px]"
                    >
                        Inbound calls
                    </ActivationSortHeadCell>
                    <ActivationSortHeadCell
                        column="phoebe-outbound"
                        sort={sort}
                        onSort={onSort}
                        className="w-[150px]"
                    >
                        Phoebe outbound
                    </ActivationSortHeadCell>
                    <ActivationSortHeadCell
                        column="inbound-transfer"
                        sort={sort}
                        onSort={onSort}
                        className="w-[270px]"
                    >
                        Inbound transfer
                    </ActivationSortHeadCell>
                    <ActivationSortHeadCell
                        column="last-inbound"
                        sort={sort}
                        onSort={onSort}
                        className="w-[130px]"
                    >
                        Last inbound
                    </ActivationSortHeadCell>
                </tr>
            </AdminTableHeader>
            <AdminTableBody>
                {accounts.map((account) => (
                    <AdminTableRow
                        key={
                            account.id ??
                            `discovered-${account.organization_id}`
                        }
                        className="h-[74px]"
                    >
                        <AdminTableCell>
                            <OrganizationCell account={account} />
                        </AdminTableCell>
                        <AdminTableCell>
                            <InlineNotesCell
                                account={account}
                                isSaving={savingFields.has(
                                    editableFieldKey(account, 'status_notes'),
                                )}
                                onSave={(statusNotes) =>
                                    onSave(account, 'status_notes', {
                                        status_notes: statusNotes,
                                    })
                                }
                            />
                        </AdminTableCell>
                        <AdminTableCell>
                            <InlineProviderCell
                                account={account}
                                isSaving={savingFields.has(
                                    editableFieldKey(account, 'phone_provider'),
                                )}
                                onSave={(provider) =>
                                    onSave(account, 'phone_provider', {
                                        phone_provider: provider,
                                    })
                                }
                            />
                        </AdminTableCell>
                        <AdminTableCell>
                            <InlineSetupCell
                                account={account}
                                isSaving={savingFields.has(
                                    editableFieldKey(
                                        account,
                                        'voip_setup_complete',
                                    ),
                                )}
                                onSave={(setupComplete) =>
                                    onSave(account, 'voip_setup_complete', {
                                        voip_setup_complete: setupComplete,
                                    })
                                }
                            />
                        </AdminTableCell>
                        <AdminTableCell>
                            <CallVolumeCell
                                total={account.inbound_call_count}
                                days={metricWindowDays}
                                breakdown={
                                    <InboundDialBreakdown account={account} />
                                }
                            />
                        </AdminTableCell>
                        <AdminTableCell>
                            <CallVolumeCell
                                total={account.phoebe_outbound_call_count}
                                days={metricWindowDays}
                            />
                        </AdminTableCell>
                        <AdminTableCell>
                            <TransferCell account={account} />
                        </AdminTableCell>
                        <AdminTableCell>
                            <LastInboundCell
                                value={account.last_inbound_call_at ?? null}
                            />
                        </AdminTableCell>
                    </AdminTableRow>
                ))}
            </AdminTableBody>
        </AdminTable>
    );
}

function OrganizationCell({ account }: { readonly account: VoipAccountRead }) {
    const organizationName = account.organization_name ?? account.account_name;
    const secondaryName =
        account.organization_internal_name &&
        account.organization_internal_name !== organizationName
            ? account.organization_internal_name
            : account.account_name !== organizationName
              ? account.account_name
              : null;

    return (
        <div className="min-w-0">
            {account.organization_id ? (
                <Link
                    to="/admin/voip/$organization-id"
                    params={{ 'organization-id': account.organization_id }}
                    className="text-admin-lg text-admin-ink hover:text-admin-ink-secondary inline-flex max-w-full items-center gap-1 font-semibold underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[color:var(--admin-ring)] focus-visible:outline-none"
                >
                    <span className="truncate">{organizationName}</span>
                    <ArrowUpRight
                        aria-hidden="true"
                        className="size-3 shrink-0"
                        strokeWidth={1.75}
                    />
                </Link>
            ) : (
                <div className="text-admin-lg text-admin-ink font-semibold">
                    {organizationName}
                </div>
            )}
            <div className="text-admin-sm text-admin-ink-muted mt-0.5 truncate">
                {secondaryName ??
                    (account.organization_id
                        ? 'Voice integration'
                        : 'Not linked')}
            </div>
        </div>
    );
}

function InlineProviderCell({
    account,
    isSaving,
    onSave,
}: {
    readonly account: VoipAccountRead;
    readonly isSaving: boolean;
    readonly onSave: (provider: VoipPhoneProvider) => Promise<void>;
}) {
    const accountLabel = account.organization_name ?? account.account_name;
    return (
        <div className="flex min-w-0 flex-col items-start gap-1">
            <div className="flex w-full items-center gap-1.5">
                <AdminSelect
                    aria-label={`Provider for ${accountLabel}`}
                    value={account.phone_provider ?? undefined}
                    placeholder={
                        account.organization_voip_provider || 'Set provider'
                    }
                    onValueChange={(provider) =>
                        void onSave(provider as VoipPhoneProvider)
                    }
                    options={VOIP_PHONE_PROVIDER_OPTIONS}
                    disabled={isSaving}
                    className="w-full"
                    containerClassName="min-w-0 flex-1"
                />
                <InlineSavingIndicator isSaving={isSaving} />
            </div>
            {account.provider_conflict ? (
                <AdminBadge tone="warn">Needs review</AdminBadge>
            ) : null}
        </div>
    );
}

function InlineSetupCell({
    account,
    isSaving,
    onSave,
}: {
    readonly account: VoipAccountRead;
    readonly isSaving: boolean;
    readonly onSave: (value: boolean) => Promise<void>;
}) {
    const accountLabel = account.organization_name ?? account.account_name;
    // Never-reviewed (null) reads as not done; picking a value persists an
    // explicit true/false.
    const value = account.voip_setup_complete === true ? 'done' : 'not-done';
    return (
        <div className="flex items-center gap-1.5">
            <AdminSelect
                aria-label={`Setup for ${accountLabel}`}
                value={value}
                onValueChange={(nextValue) => void onSave(nextValue === 'done')}
                options={[
                    { value: 'done', label: 'Done' },
                    { value: 'not-done', label: 'Not done' },
                ]}
                disabled={isSaving || !account.organization_id}
                className="w-[118px]"
            />
            <InlineSavingIndicator isSaving={isSaving} />
        </div>
    );
}

function CallVolumeCell({
    total,
    days,
    breakdown,
}: {
    readonly total: number;
    readonly days: number;
    readonly breakdown?: React.ReactNode;
}) {
    return (
        <div className="tabular-nums">
            <div className="text-admin-xl text-admin-ink font-semibold tracking-[-0.015em]">
                {formatDailyAverage(total, days)}
                <span className="text-admin-xs text-admin-ink-muted ml-1 font-normal">
                    / day
                </span>
            </div>
            <div className="text-admin-sm text-admin-ink-muted mt-0.5">
                {total.toLocaleString()} total · {days}d
            </div>
            {breakdown}
        </div>
    );
}

/**
 * Splits inbound volume by what the caller dialed: the org's Phoebe number
 * directly, or the office line that forwards into Phoebe. Hidden when no
 * calls arrived via an office-line forward. Hover shows the actual numbers.
 */
function InboundDialBreakdown({
    account,
}: {
    readonly account: VoipAccountRead;
}) {
    const forwarded = account.inbound_forwarded_call_count;
    if (forwarded === 0) {
        return null;
    }
    const direct = account.inbound_call_count - forwarded;
    const phoebeNumbers = (account.phone_numbers ?? [])
        .filter((phoneNumber) => phoneNumber.voice_enabled)
        .map((phoneNumber) => phoneNumber.number);
    const officeNumbers = (account.forwarding_lines ?? []).map(
        (line) => line.external_main_number,
    );
    const title = [
        phoebeNumbers.length > 0
            ? `Phoebe direct: ${phoebeNumbers.join(', ')}`
            : null,
        officeNumbers.length > 0
            ? `Office line: ${officeNumbers.join(', ')}`
            : null,
    ]
        .filter((line): line is string => line !== null)
        .join('\n');
    return (
        <div
            className="text-admin-xs text-admin-ink-faint mt-0.5"
            title={title || undefined}
        >
            {direct.toLocaleString()} direct · {forwarded.toLocaleString()}{' '}
            office line
        </div>
    );
}

function TransferCell({ account }: { readonly account: VoipAccountRead }) {
    const transferred = account.inbound_transferred_call_count;
    const notTransferred = account.inbound_not_transferred_call_count;
    const unknown = account.inbound_transfer_unknown_call_count;
    const transferPercent = classifiedTransferPercent(
        transferred,
        notTransferred,
    );

    if (account.inbound_call_count === 0) {
        return (
            <span className="text-admin-sm text-admin-ink-faint">
                No inbound calls
            </span>
        );
    }
    if (transferPercent === null) {
        return (
            <div className="text-admin-sm text-admin-ink-muted tabular-nums">
                Transfer status unavailable
                <div className="text-admin-xs text-admin-ink-faint mt-1">
                    {unknown.toLocaleString()} unclassified
                </div>
            </div>
        );
    }

    return (
        <div className="min-w-0 tabular-nums">
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-admin-lg text-admin-ink font-semibold">
                    {Math.round(transferPercent)}% transferred
                </span>
                {unknown > 0 ? (
                    <span className="text-admin-xs text-admin-ink-faint">
                        {unknown.toLocaleString()} unclassified
                    </span>
                ) : null}
            </div>
            <div
                className="bg-admin-border mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full"
                role="img"
                aria-label={`${transferred} transferred, ${notTransferred} no transfer${unknown ? `, ${unknown} unclassified` : ''}`}
            >
                <span
                    className="bg-admin-warn-text h-full"
                    style={{ width: `${transferPercent}%` }}
                />
                <span
                    className="bg-admin-border-strong h-full"
                    style={{ width: `${100 - transferPercent}%` }}
                />
            </div>
            <div className="text-admin-xs text-admin-ink-muted mt-1 flex items-center gap-3">
                <span>{transferred.toLocaleString()} transferred</span>
                <span>{notTransferred.toLocaleString()} no transfer</span>
            </div>
        </div>
    );
}

function LastInboundCell({ value }: { readonly value: string | null }) {
    if (!value) {
        return (
            <span className="text-admin-sm text-admin-ink-faint">
                None in period
            </span>
        );
    }
    const date = new Date(value);
    return (
        <div title={date.toLocaleString()}>
            <div className="text-admin-base text-admin-ink-secondary">
                {formatDistanceToNowStrict(date, { addSuffix: true })}
            </div>
            <div className="text-admin-xs text-admin-ink-faint mt-0.5">
                {date.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                })}
            </div>
        </div>
    );
}

function InlineNotesCell({
    account,
    isSaving,
    onSave,
}: {
    readonly account: VoipAccountRead;
    readonly isSaving: boolean;
    readonly onSave: (value: string) => Promise<void>;
}) {
    const [value, setValue] = useState(account.status_notes);
    const valueRef = useRef(account.status_notes);
    useEffect(() => {
        valueRef.current = account.status_notes;
        setValue(account.status_notes);
    }, [account.status_notes]);
    const accountLabel = account.organization_name ?? account.account_name;

    const commit = () => {
        const nextValue = valueRef.current.trim();
        if (nextValue !== account.status_notes) {
            void onSave(nextValue);
        }
    };

    return (
        <div className="relative min-w-0">
            <AdminInput
                aria-label={`Notes for ${accountLabel}`}
                value={value}
                placeholder="Add a note…"
                disabled={isSaving}
                className="pr-7"
                onChange={(event) => {
                    valueRef.current = event.target.value;
                    setValue(event.target.value);
                }}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        event.currentTarget.blur();
                    } else if (event.key === 'Escape') {
                        valueRef.current = account.status_notes;
                        setValue(account.status_notes);
                        event.currentTarget.blur();
                    }
                }}
            />
            <span className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2">
                <InlineSavingIndicator isSaving={isSaving} />
            </span>
        </div>
    );
}

function InlineSavingIndicator({ isSaving }: { readonly isSaving: boolean }) {
    return isSaving ? (
        <LoaderCircle
            aria-label="Saving"
            className="text-admin-ink-muted size-3.5 animate-spin motion-reduce:animate-none"
        />
    ) : null;
}
